import * as React from 'react';

import { DawnDotsDecoration } from '../../atoms/DawnDotsDecoration';
import { cn } from '../../lib/utils';

export interface OnboardingCardProps {
  children?: React.ReactNode;
  /**
   * Drop the bottom-right Dawn-dots watermark. The refreshed wizard card
   * (Figma `2776:8592`) is plain white.
   */
  noWatermark?: boolean;
  className?: string;
}

/**
 * OnboardingCard — the white inner content card of the onboarding shell (Figma
 * `5:2640`). A dumb presentational shell: fixed geometry, white background,
 * rounded corners, `p-10`, and the bottom-right Dawn-dots watermark. Callers own
 * all content and CTAs via `children`. The decoration uses `-z-10` so it always
 * paints behind in-flow content for every caller, with no per-caller z discipline.
 */
export const OnboardingCard = React.forwardRef<HTMLDivElement, OnboardingCardProps>(
  ({ children, noWatermark, className }, ref) => (
    <div
      ref={ref}
      className={cn(
        'relative isolate overflow-clip bg-dawn-white rounded-lg p-10 flex flex-col',
        'w-[470px] h-[456px] min-w-[281px] min-h-[280px]',
        className
      )}
    >
      {children}
      {/* isolate on the card makes it a stacking context, so this -z-10 watermark
          paints above the card's white bg but behind in-flow content (without
          isolate, -z-10 escapes and hides behind the white bg entirely). */}
      {/* Anchored to the bottom-right corner (not left/top) so the watermark
          keeps its designed bleed when a caller resizes the card. At the
          default 470×456 this is pixel-identical to Figma's left-273/top-262. */}
      {!noWatermark && (
        <DawnDotsDecoration className="absolute -z-10 w-[213px] h-[213px] -bottom-[19px] -right-4" />
      )}
    </div>
  )
);

OnboardingCard.displayName = 'OnboardingCard';
